/**
 * 2FA API Service
 * Handles API calls for stateless challenge token OTP verification and resend
 * 
 * These API calls use the challenge token pattern:
 * - Challenge token is retrieved from sessionStorage
 * - Token is included in request body (not header)
 * - Server validates and returns new token on failed attempts
 * - No pod-local state required (fully stateless)
 */

import axios from 'axios';
import config from '../sso/config';
import { 
  retrieveChallengeToken, 
  updateChallengeToken,
  clearChallengeToken 
} from './2fa-token.service';

const DEFAULT_OTP_ATTEMPTS = 3;

/**
 * Verify OTP with challenge token
 * POST /verifyOTP with {challengeToken, otp}
 * 
 * Success Response (200):
 *   { status: 200, redirectURL: '/projects' }
 *   → JWT cookies set by server
 *   → Redirect to projects
 * 
 * Error Response (401):
 *   { status: 401, challengeToken: newToken, remainingAttempts: 4 }
 *   → Invalid OTP or token expired
 *   → Update token and retry
 * 
 * Error Response (429):
 *   { status: 429 }
 *   → Rate limited - too many attempts
 *   → Show message, try again later
 * 
 * Error Response (423):
 *   { status: 423 }
 *   → Account locked - exceeded max attempts
 *   → Show message, contact support
 * 
 * @param {string} otpCode - The 6-digit OTP code from user input
 * @returns {Promise} Promise resolving to API response
 */
export const verifyOtp = async (otpCode) => {
  try {
    /* Get challenge token from sessionStorage */
    const challengeToken = retrieveChallengeToken();

    if (!challengeToken) {
      return {
        status: 401,
        error: 'Session expired. Please login again.',
        details: 'Challenge token not available'
      };
    }

    /* Validate OTP format */
    if (!otpCode || !/^\d{6}$/.test(otpCode)) {
      return {
        status: 400,
        error: 'Invalid OTP format. Must be 6 digits.',
        details: 'OTP validation failed'
      };
    }

    /* POST to /verifyOTP with challenge token */
    const response = await axios.post(
      `${config.express_base_url}verifyOTP`,
      {
        challengeToken,  // ← CRITICAL: Echo back challenge token
        otp: otpCode      // ← User-entered OTP
      },
      {
        withCredentials: true,  // ← Include cookies in request
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    /* Handle successful verification */
    if (response.data && response.data.status === 200) {
      clearChallengeToken();  // Clean up storage
      return response.data;
    }

    /* Handle unsuccessful response */
    return response.data;

  } catch (error) {
    /* Handle network errors */
    if (!error.response) {
      return {
        status: 500,
        error: 'Network error. Please check your connection.',
        details: error.message
      };
    }

    /* Handle server errors - extract new token if provided */
    const { status, data } = error.response;

    /* On 401, check if server returned new token (incremented attempt counter) */
    if (status === 401 && data?.challengeToken) {
      return {
        status: 401,
        error: data.error || 'Invalid OTP. Please try again.',
        challengeToken: data.challengeToken,  // ← New token with incremented counter
        remainingAttempts: data.remainingAttempts || DEFAULT_OTP_ATTEMPTS
      };
    }

    /* Return error response as-is (429, 423, other errors) */
    return {
      status,
      error: data?.error || `Verification failed (${status})`,
      details: data
    };
  }
};

/**
 * Resend OTP with challenge token
 * POST /resendOTP with {challengeToken}
 * 
 * Success Response (200):
 *   { status: 200 }
 *   → New OTP sent to email
 *   → Show message to user
 * 
 * Error Response (429):
 *   { status: 429 }
 *   → Rate limited - too many resend attempts (5 per 24h)
 *   → Show message, try again later or contact support
 * 
 * Error Response (423):
 *   { status: 423 }
 *   → Account locked - exceeded max resend attempts
 *   → Show message, contact support
 * 
 * Error Response (401):
 *   { status: 401 }
 *   → Token expired - must login again
 *   → Redirect to preLogin
 * 
 * @returns {Promise} Promise resolving to API response
 */
export const resendOtp = async () => {
  try {
    /* Get challenge token from sessionStorage */
    const challengeToken = retrieveChallengeToken();

    if (!challengeToken) {
      return {
        status: 401,
        error: 'Session expired. Please login again.',
        details: 'Challenge token not available'
      };
    }

    /* POST to /resendOTP with challenge token */
    const response = await axios.post(
      `${config.express_base_url}resendOTP`,
      {
        challengeToken  // ← CRITICAL: Pass challenge token
      },
      {
        withCredentials: true,  // ← Include cookies in request
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    /* Handle successful resend */
    if (response.data && response.data.status === 200) {
      return response.data;
    }

    /* Handle unsuccessful response */
    return response.data;

  } catch (error) {
    /* Handle network errors */
    if (!error.response) {
      return {
        status: 500,
        error: 'Network error. Please check your connection.',
        details: error.message
      };
    }

    /* Return server error response as-is (429, 423, 401, etc.) */
    const { status, data } = error.response;
    return {
      status,
      error: data?.error || `Resend failed (${status})`,
      details: data
    };
  }
};

/**
 * Extract safe error message for UI display
 * Maps error status codes to user-friendly messages
 * @param {number} status - HTTP status code
 * @param {string} defaultMessage - Default message if status not recognized
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (status, defaultMessage = 'An error occurred') => {
  const messages = {
    400: 'Invalid OTP format. Must be 6 digits.',
    401: 'Invalid OTP or session expired. Please try again.',
    429: 'Too many attempts. Please wait and try again.',
    423: 'Your account is temporarily locked. Please try again later.',
    500: 'Server error. Please try again.',
    undefined: 'Network error. Please check your connection.'
  };

  return messages[status] || defaultMessage;
};

/**
 * Extract safe info message for UI display
 * @param {number} remainingAttempts - Number of remaining attempts
 * @returns {string} Attempts info message
 */
export const getAttemptsMessage = (remainingAttempts) => {
  if (!remainingAttempts || remainingAttempts <= 0) {
    return 'No remaining attempts. Please contact support.';
  }
  if (remainingAttempts === 1) {
    return 'Last attempt remaining. Be careful.';
  }
  return `${remainingAttempts} attempt(s) remaining`;
};

export default {
  verifyOtp,
  resendOtp,
  getErrorMessage,
  getAttemptsMessage
};
